import { getAnalysisErrorMessage } from '@shared/models/error-code.model';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideLoader, lucideTriangleAlert } from '@ng-icons/lucide';
import {
  AnalysisTechnicalStatus,
  normalizeTechnicalState,
} from '@shared/models/analysis.model';
import { TimelineStep } from '@shared/models/step-timeline.model';
import { StepTimeline } from '@shared/components/step-timeline/step-timeline';
import { type PipelineAction } from '@features/analyses/services/analysis-workflow.facade';

interface TaskRuntime {
  status?: string;
  state?: string;
  duration?: string;
  durationMs?: number;
  message?: string;
  resultSnippet?: string;
  [key: string]: unknown;
}

type DetailTaskUiState = 'done' | 'todo' | 'current' | 'failed';

interface DetailedTaskEntry {
  taskNo: number;
  label: string;
  description: string;
  task: TaskRuntime;
  uiState: DetailTaskUiState;
  uiStateLabel: 'Erledigt' | 'Ausstehend' | 'Läuft' | 'Fehlgeschlagen';
  errorCode?: string;
  errorMessage?: string;
  errorDetail?: string;
}

export interface DebugTaskActionExecution {
  action: PipelineAction;
  taskNo: number;
  trigger: 'rerun' | 'start';
}

@Component({
  selector: 'app-analysis-detail-debug-panel',
  standalone: true,
  imports: [NgIconComponent, StepTimeline],
  providers: [provideIcons({ lucideLoader, lucideTriangleAlert })],
  templateUrl: './analysis-detail-debug-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisDetailDebugPanel {
  analysisId = input('');
  createdAt = input<Date | string | null>(null);
  runtime = input<Record<string, unknown> | null>(null);
  technicalStatus = input<AnalysisTechnicalStatus | null>(null);
  isResettingState = input(false);

  actionExecuted = output<DebugTaskActionExecution>();
  resetStateRequested = output<void>();

  private readonly siTasks = [
    { taskNo: 1, label: 'GeoCut', description: 'Geodaten vorbereiten und zuschneiden' },
    { taskNo: 2, label: 'Detection', description: 'Dachobjekte erkennen' },
    { taskNo: 3, label: 'Orientation', description: 'Dachausrichtung bestimmen' },
    { taskNo: 4, label: 'Max Placement', description: 'Maximale Modulbelegung berechnen' },
    { taskNo: 5, label: 'Planning', description: 'PV-Planung berechnen' },
  ] as const;

  private readonly runtimeTaskMap = computed(() => {
    const map = new Map<number, TaskRuntime>();
    const tasks = this.runtime()?.['tasks'];
    if (!tasks || typeof tasks !== 'object') return map;

    if (Array.isArray(tasks)) {
      (tasks as Record<string, unknown>[]).forEach((entry, index) => {
        const taskNo = Number(entry?.['taskNumber'] ?? entry?.['task'] ?? index + 1);
        if (Number.isFinite(taskNo) && taskNo > 0) map.set(taskNo, entry ?? {});
      });
      return map;
    }

    Object.entries(tasks as Record<string, unknown>).forEach(([rawTaskNo, task]) => {
      const taskNo = Number(rawTaskNo);
      if (Number.isFinite(taskNo) && taskNo > 0) map.set(taskNo, (task ?? {}) as TaskRuntime);
    });
    return map;
  });

  taskEntries = computed<DetailedTaskEntry[]>(() => {
    const technicalStatus = this.technicalStatus();
    const currentTask = Number(technicalStatus?.task ?? 0);
    const progressState = this.normalizeStatus(technicalStatus?.state);
    const errorCode = String(technicalStatus?.errorCode || '');
    const errorMessage = String(technicalStatus?.errorMessage || '');
    const errorDetail = String(technicalStatus?.errorDetail || '');
    const completedAt = String(technicalStatus?.completedAt || '');

    return this.siTasks.map((definition) => {
      const task = this.runtimeTaskMap().get(definition.taskNo) || {};
      const status = this.deriveStatus(
        definition.taskNo,
        this.normalizeStatus(task.status || task.state),
        progressState,
        currentTask,
        errorCode,
        completedAt,
      );
      const uiState = this.toUiState(status);
      const isCurrentFailure = uiState === 'failed' && currentTask === definition.taskNo;

      return {
        ...definition,
        task,
        uiState,
        uiStateLabel: this.getUiStateLabel(uiState),
        errorCode: String(task['errorCode'] || task['error_code'] || (isCurrentFailure ? errorCode : '')) || undefined,
        errorMessage: getAnalysisErrorMessage(
          String(task['errorCode'] || task['error_code'] || (isCurrentFailure ? errorCode : '')),
          String(task['errorMessage'] || task['error_message'] || task.message || (isCurrentFailure ? errorMessage : '')),
        ),
        errorDetail: String(task['errorDetail'] || task['error_detail'] || (isCurrentFailure ? errorDetail : '')) || undefined,
      };
    });
  });

  nextTaskNo = computed(() => this.taskEntries().find((entry) => entry.uiState === 'todo')?.taskNo ?? null);
  failedEntry = computed(() => this.taskEntries().find((entry) => entry.uiState === 'failed') ?? null);
  shortAnalysisId = computed(() => this.analysisId().slice(-8) || '—');
  workerLabel = computed(() => String(this.runtime()?.['worker'] || this.runtime()?.['workerName'] || this.runtime()?.['worker_id'] || '—'));
  runtimeLabel = computed(() => {
    const runtime = this.runtime();
    const total = runtime?.['duration'] || runtime?.['totalDuration'] || runtime?.['runtime'];
    if (total) return String(total);
    const current = this.taskEntries().find((entry) => entry.uiState === 'current');
    return this.formatDuration(current?.task) || '—';
  });

  technicalTimelineSteps = computed<TimelineStep[]>(() => [
    {
      id: 'created',
      label: 'Erstellt',
      description: 'Angelegt',
      state: 'completed',
      nodeText: '·',
      meta: this.formatCreatedAt(this.createdAt()),
    },
    ...this.taskEntries().map((entry) => this.toTimelineStep(entry)),
  ]);

  private toTimelineStep(entry: DetailedTaskEntry): TimelineStep {
    const action = entry.uiState === 'current'
      ? this.createCancelAction()
      : this.getRerunAction(entry.taskNo, entry.uiState) || this.getStartAction(entry.taskNo);
    const trigger = entry.uiState === 'done' || entry.uiState === 'failed' ? 'rerun' : 'start';

    return {
      id: entry.taskNo,
      label: entry.label,
      description: this.getProgressLabel(entry),
      state: entry.uiState === 'done' ? 'completed' : entry.uiState === 'current' ? 'current' : entry.uiState === 'failed' ? 'error' : 'upcoming',
      nodeActionIcon: action
        ? entry.uiState === 'current' ? 'lucideSquare' : trigger === 'rerun' ? 'lucideRotateCcw' : 'lucidePlay'
        : undefined,
      nodeText: action ? undefined : String(entry.taskNo),
      nodeActionDisabled: action ? !action.hasPermission || action.disabled : false,
      nodeActionTitle: action?.description || action?.label,
      meta: this.formatDuration(entry.task),
      note: entry.errorDetail || entry.errorMessage || entry.task.resultSnippet || entry.task.message,
      data: action ? { taskNo: entry.taskNo, trigger, action } : undefined,
    };
  }

  private getProgressLabel(entry: DetailedTaskEntry): string {
    if (entry.uiState !== 'current') return entry.uiStateLabel;
    const progress = Number(this.technicalStatus()?.progressPercent);
    return Number.isFinite(progress)
      ? `${entry.uiStateLabel} · ${Math.round(Math.min(100, Math.max(0, progress)))}%`
      : entry.uiStateLabel;
  }

  onTimelineNodeAction(step: TimelineStep): void {
    const action = step.data?.['action'] as PipelineAction | undefined;
    const taskNo = Number(step.data?.['taskNo']);
    const trigger = step.data?.['trigger'];
    if (action && Number.isFinite(taskNo) && (trigger === 'rerun' || trigger === 'start')) {
      this.onActionExecute(action, taskNo, trigger);
    }
  }

  getRerunAction(taskNo: number, uiState: DetailTaskUiState): PipelineAction | null {
    if (uiState !== 'done' && uiState !== 'failed') return null;
    return taskNo >= 4 ? this.createStartPlanningAction() : this.createStartAnalysisAction();
  }

  getStartAction(taskNo: number): PipelineAction | null {
    if (this.nextTaskNo() !== taskNo) return null;
    if (this.taskEntries().some((entry) => entry.taskNo < taskNo && entry.uiState === 'failed')) return null;
    return taskNo >= 4 ? this.createStartPlanningAction() : this.createStartAnalysisAction();
  }

  onActionExecute(action: PipelineAction, taskNo: number, trigger: 'rerun' | 'start'): void {
    if (!action.hasPermission || action.disabled) return;
    this.actionExecuted.emit({ action, taskNo, trigger });
  }

  private createStartAnalysisAction(): PipelineAction {
    return { action: 'start-analysis', targetStatus: 'analyzing', label: 'Analyse starten', description: 'Ab dieser Aufgabe ausführen', hasPermission: true, requiredPermission: 'analysis:start_analysis', disabled: false };
  }

  private createStartPlanningAction(): PipelineAction {
    return { action: 'start-planning', targetStatus: 'planning', label: 'Planung starten', description: 'Ab dieser Aufgabe ausführen', hasPermission: true, requiredPermission: 'analysis:start_planning', disabled: false };
  }

  private createCancelAction(): PipelineAction {
    return { action: 'cancel-analysis', label: 'Ausführung stoppen', description: 'Laufende Ausführung stoppen', hasPermission: true, requiredPermission: 'analysis:start_analysis', disabled: false };
  }

  private normalizeStatus(status?: string): string {
    return status ? normalizeTechnicalState(status) : '';
  }

  private toUiState(status: string): DetailTaskUiState {
    if (status === 'completed') return 'done';
    if (status === 'failed') return 'failed';
    if (status === 'processing' || status === 'queued') return 'current';
    return 'todo';
  }

  private getUiStateLabel(state: DetailTaskUiState): DetailedTaskEntry['uiStateLabel'] {
    if (state === 'done') return 'Erledigt';
    if (state === 'current') return 'Läuft';
    if (state === 'failed') return 'Fehlgeschlagen';
    return 'Ausstehend';
  }

  private deriveStatus(taskNo: number, runtimeStatus: string, progressState: string, currentTask: number, errorCode: string, completedAt: string): string {
    if (errorCode && currentTask === taskNo) return 'failed';
    if (errorCode) return taskNo < currentTask ? 'completed' : 'pending';
    if (runtimeStatus === 'failed') return 'failed';
    if (progressState === 'processing' || progressState === 'queued') {
      if (taskNo < currentTask) return 'completed';
      return taskNo === currentTask ? progressState : 'pending';
    }
    if (progressState === 'completed' || completedAt) return taskNo <= currentTask && currentTask > 0 ? 'completed' : 'pending';
    if (progressState === 'failed' && currentTask === taskNo) return 'failed';
    if (runtimeStatus === 'processing' || runtimeStatus === 'queued') {
      return taskNo === currentTask || (currentTask <= 0 && taskNo === 1)
        ? runtimeStatus
        : 'pending';
    }
    if (runtimeStatus) return runtimeStatus;
    if (currentTask <= 0) return taskNo === 1 ? 'queued' : 'pending';
    if (taskNo < currentTask) return 'completed';
    return taskNo === currentTask ? 'processing' : 'pending';
  }

  private formatDuration(task?: TaskRuntime): string | undefined {
    if (task?.duration) return String(task.duration);
    if (task?.durationMs != null) return `${task.durationMs} ms`;
    return undefined;
  }

  private formatCreatedAt(value: Date | string | null): string | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
  }
}
